<template>
  <div class="container checkoutPage">
    <div class="has-text-centered gant-text mb-5">
      <h1 class="title gant-text">Select a booking class</h1>
    </div>

    <div class="boxWithBorder mb-3">
      <!--NDC Warning-->
      <b-message title="Airline Direct (NDC) Fare" type="is-success" aria-close-label="Close message">
        <ul>
          <li>
            This fare is a direct airline fare (NDC) and may contain
            special pricing.
          </li>
        </ul>
      </b-message>
      <!--/NDC Warning-->

      <div class="p-4">

        <div class="columns">
          <div class="column">
            <h3 class="is-size-5 has-text-weight-bold mb-3">
              Passenger:
              {{ traveler.firstName + " " + traveler.lastName }}
            </h3>

            <div v-if="loading" class="is-size-4 has-font-weight-bold has-text-left" data-test="show_loading">
              <div class="is-size-5">Getting latest fare details...</div>
              <b-skeleton size="is-large" width="20%" :animated="true"></b-skeleton>
              <b-skeleton size="is-large" width="35%" :animated="true"></b-skeleton>
              <b-skeleton size="is-large" width="50%" :animated="true"></b-skeleton>
            </div>

            <div v-else>
              <div class="columns is-mobile">
                <!-- AIRLINE LOGO -->
                <div class="column is-narrow has-text-centered wrap">
                  <div class="mt-1">
                    <img width="30" :src="$helpers.airlineLogo(flight.airlineCode, 30, 30)" />
                  </div>
                </div>
                <!-- FROM-TO -->
                <div class="column">
                   <div class="has-text-weight-semibold">
                    {{ $helpers.airlineName(flight.airlineCode) }}
                  </div> 
                  <div class="columns">
                    <JourneySummaryAlt v-for="(journey, journeyIndex) in flight.journeys" :key="journeyIndex"
                      :journey="journey" :index="journeyIndex" class="column is-narrow" />
                  </div>
                </div>
              </div>
            </div>

            <b-notification v-if="failed" type="is-danger" :closable="false" position="is-top-right" has-icon icon="clock"
              icon-size="is-small">
              <div class="is-size-5 mt-2">
                Sorry, we were unable to retrieve fare details for this
                selection. Please try again or choose another flight.
              </div>
            </b-notification>

            <div class="mt-5 buttons" v-if="failed">
              <b-button icon-left="refresh" type="is-danger" @click="updateKey++">
                Retry
              </b-button>

              <b-button icon-right="arrow-right" type="is-info" @click="removeFlight()">
                Delete flight and continue
              </b-button>
            </div>


          </div>

          <div class="column is-narrow">
            <!-- Fare Expiration -->
            <b-notification
              v-if="priceData"
              :type="countdown > 0 ? 'is-warning' : 'is-danger'"
              :closable="false"
              position="is-top-right"
              has-icon
              icon="clock"
              icon-size="is-small"
            >
              <div v-if="countdown > 0" class="is-size-5 mt-2">
                Hurry! This fare expires in {{ minutes }} minutes
                {{ seconds }} seconds.
              </div>
              <div v-else class="is-size-5 mt-2">
                Sorry, this fare has expired.
              </div>
            </b-notification>
            <!-- /Fare Expiration -->
          </div>


        </div>
        <div class="columns">
      <div class="column">
        <HiddenResultsNotification
            v-if="hiddenOffersCount > 0"
            :hiddenItemsCount="hiddenOffersCount"
          />
      </div>
    </div>
        <div class="columns is-multiline">
          <!-- Column for the initial "selected" NDC fare option -->
          <div class="column is-4" v-if="priceData">
            <NDCFareOption :flight="flight" :offer="priceData.pricedOffer" :guest="guest" />
          </div>

          <!-- Columns for other offers -->
          <template v-if="visibleOtherOffers.length > 0 && priceData.pricedOffer">
            <div v-for="(offer, index) in visibleOtherOffers" :key="index" class="column is-4">
              <NDCFareOption :flight="offer.solution" :offer="offer" :guest="guest" />
            </div>
          </template>

        </div>


      </div>
    </div>
  </div>
</template>

<script>
import NDCFareOption from "./NDCFareOption.vue";
import JourneySummaryAlt from "./JourneySummaryAlt";
import meetingsMixin from "@/mixins/meetingsMixin";
import moment from "moment";
import HiddenResultsNotification from "../HiddenResultsNotification.vue";

export default {
  name: "OfferPricePage",
  mixins: [meetingsMixin],
  props: {},
  components: {
    NDCFareOption,
    JourneySummaryAlt,
    HiddenResultsNotification,
  },
  data() {
    return {
      priceData: null,
      countdown: null,
      loading: false,
      failed: false,
      updateKey: 0,
    };
  },
  watch: {
    updateKey: {
      handler: function (val, oldVal) {
        if (val) {
          this.getOfferPrice();
        }
      },
    },
  },
  methods: {
    removeFlight() {
      let payload = {
        flight: {},
        offer: {},
      };

      if (this.guest) {
        let index = this.guests.findIndex(
          (guest) =>
            guest.meetingAttendeeGuestId === this.guest.meetingAttendeeGuestId
        );
        this.$store.commit("bookTravel/DEL_GUEST_TRIP", index);
      }

      this.$store.dispatch("bookTravel/setNdcBooking", payload);
      this.goToNext();
    },
    goToNext() {
      if (this.pathMixin_isMeeting) {
        this.meetingRouter();
      } else {
        this.$nav.pushRouteTo({ name: "BookingItinerary" });
      }
    },
    countdownTimer() {
      if (this.countdown > 0) {
        setTimeout(() => {
          this.countdown -= 1;
          this.countdownTimer();
        }, 1000);
      }
    },
    async getOfferPrice() {
      this.loading = true;
      this.failed = false;

      let body = {
        includeUpsells: true,
        ownerCode: this.selectedOffer.ownerCode,
        responseId: this.selectedOffer.responseId,
        offerId: this.selectedOffer.offerId,
        items: this.selectedOffer.items,
        solution: this.flight,
        applyPolicy: false,
        travelerId: this.$globals.travellerProfile.id,
      };

      if (
        this.$store.getters["travellersAndTrips/getTravellers"].tripType ===
        "business"
      ) {
        body.travelPolicyId = this.$globals.travellerProfile.travelPolicyId;
        body.applyPolicy = true;
        body.benchmarkPrice = this.flight.benchmarkPrice;
      }

      let result = await this.$app.huddleLabsServerCall(
        "amadeus/travel/offer-price",
        body
      );

      this.loading = false;

      if (result.data) {
        this.priceData = result.data;

        //update the SelectedOffer using values returned by call to travel/offer-price
        let selectedOffer = this.priceData.pricedOffer;

        //filter out optional (upsell) items
        selectedOffer.items = this.priceData.pricedOffer.items.filter(
          (item) => item.required === true
        );

        // Create a copy of pricedOffer to preserve the original offerId and responseId
        // This is needed for seat map requests which require the priced offer's IDs
        let pricedOfferForSeatMap = {
          offerId: this.priceData.pricedOffer.offerId,
          ownerCode: this.priceData.pricedOffer.ownerCode,
          responseId: this.priceData.pricedOffer.responseId,
        };

        let payload = {
          flight: this.flight,
          offer: selectedOffer,
          pricedOffer: pricedOfferForSeatMap,
        };

        if (this.guest) {
          payload.guestId = this.guest.meetingAttendeeGuestId;
        }

        this.$store.dispatch("bookTravel/setNdcBooking", payload);

        let expireTime = moment.utc(this.priceData.pricedOffer.expireTime);
        this.countdown =
          expireTime > moment.utc()
            ? Math.abs(moment().diff(expireTime, "seconds"))
            : 0;

        this.countdownTimer();
      } else {
        this.failed = true;
      }
    },
  },

  computed: {
    traveler() {
      if (this.guest) {
        return this.guest.profile;
      } else {
        return this.$store.state.travellersAndTrips.currentTravellerProfile;
      }
    },
    fareDescription() {
      return this.selectedOffer?.bookingClass;
    },
    otherOffers() {
      return this.priceData?.otherOffers || [];
    },
    visibleOtherOffers() {
      return this.otherOffers.filter(i => i.solution.isVisible);
    },
    hiddenOffersCount() {
      return Math.max(this.otherOffers.length - this.visibleOtherOffers.length, 0);
    },
    flight() {
      if (this.guest) {
        return this.guest.result.flight;
      } else {
        return this.$store.state.bookTravel.result.flight;
      }
    },
    guestId() {
      return this.$route.params.guestId;
    },
    guest() {
      let guests = this.$store.state.bookTravel.guests;
      return guests[0];
    },
    selectedOffer() {
      if (this.guest) {
        return this.guest.result.selectedOffer;
      } else {
        return this.$store.state.bookTravel.result.selectedOffer;
      }
    },
    minutes() {
      return Math.floor(this.countdown / 60);
    },
    seconds() {
      return this.countdown % 60;
    },
    optionMaxWidth() {
      if (this.pathMixin_isMeeting) {
        const styleObject = {
          "max-width": "800px",
        };
        return styleObject;
      } else {
        return "";
      }
    },
  },
  async created() {
    if (this.selectedOffer) {
      this.getOfferPrice();
    }
  },
};
</script>

<style lang="scss" scoped>
.custom-notification {
  padding: 0;
}
</style>
